<template>
  <div
    v-if="messages.length"
    :class="{ 'organizer-message-outside-block': !!title }"
    :dir="isHebrew ? 'rtl' : 'ltr'"
  >
    <p v-if="title" class="organizer-message-outside-title">{{ title }}</p>
    <div class="organizer-message-day-list">
      <div
        v-for="message in messages"
        :key="message.id"
        class="organizer-message-day-item"
        @click="$emit('edit', message)"
      >
        <img
          :src="require('@/assets/images/icons/icons8-whatsapp-50.svg')"
          alt=""
          class="organizer-message-whatsapp-icon organizer-message-day-item-icon">
        <span class="organizer-message-day-item-time">{{ rowLabel(message) }}</span>
        <span
          class="organizer-message-recipient"
          :class="{ 'is-private': organizerMessageIsPrivate(message) }"
        >{{ organizerRecipientLabel(message) }}</span>
        <!-- INFORMATION, never a control. There is no retry for a missed
             message and no un-send for a delivered one, so a button here
             would promise something that does not exist. 'missed' is the
             one that was invisible: the scan skips a message whose moment
             passed more than six hours ago and says nothing, which is how a
             COPIED ride arrives carrying messages that will never go out. -->
        <span
          v-if="deliveryLabel(message)"
          class="organizer-message-delivery"
          :class="'is-' + message.delivery"
          :title="deliveryLabel(message)"
        >{{ deliveryLabel(message) }}</span>
        <span class="organizer-message-day-item-body">{{ truncateBody(message.body) }}</span>
        <span
          class="material-icons organizer-message-day-item-delete"
          :title="t('organizerMessageDeleteAction')"
          @click.stop="$emit('remove', message)">close</span>
      </div>
    </div>
  </div>
</template>

<script>
// A list of scheduled WhatsApp organizer-message rows.
//
// One component for all three placements, because they are the same row:
//   - under a trip day, in the list view (no title, no date -- the day
//     header above already states it)
//   - before / after the trip, in BOTH views (title and date, because
//     there is no day header to supply either)
//
// It exists to avoid the alternative, which was this markup plus its CSS
// plus three formatting helpers copied into SingleRide.vue and
// TripCalendarView.vue. The owner's standing instruction for this feature
// is one implementation shared by both views, not a per-view copy.
//
// Deliberately dumb: it owns presentation only. It holds no gate, does no
// fetching, and decides nothing about which messages it is given -- the
// private-ride manager gate (canManageOrganizerMessages) and the before/on/after
// split (partitionOrganizerMessages) both stay with the caller, so there is
// exactly one place each of those can be got wrong. Clicks leave as `edit`
// and `remove` events; the parent owns the modal and the delete call.
import moment from 'moment';
import { translationMixin } from '@/mixins/translationMixin';
import {
  organizerMessageIsPrivateFor,
  organizerRecipientLabelFor
} from '@/mixins/organizerMessagesMixin';

export default {
  name: 'OrganizerMessageList',
  mixins: [translationMixin],
  props: {
    messages: {
      type: Array,
      required: true
    },
    // Empty renders no heading and no surrounding block -- that is the
    // under-a-day-header case.
    title: {
      type: String,
      default: ''
    },
    // Prefixes each row with its date. Needed only where no day header
    // states it, i.e. the before/after blocks.
    showDate: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    // Not the organizerMessagesMixin methods: this component is deliberately
    // dumb and must not mix in that mixin's fetching watchers.
    organizerMessageIsPrivate( message ) {
      return organizerMessageIsPrivateFor( message );
    },
    organizerRecipientLabel( message ) {
      return organizerRecipientLabelFor( message, this.t );
    },

    deliveryLabel( message ) {
      // Only the two settled states say anything. 'scheduled' is the
      // ordinary case and needs no badge -- labelling every row would bury
      // the two that matter.
      if ( message.delivery === 'sent' ) return this.t( 'organizerMessageWasSent' );
      if ( message.delivery === 'missed' ) return this.t( 'organizerMessageMissed' );
      return '';
    },
    rowLabel( message ) {
      const time = this.formatTime(message.send_time);
      if ( !this.showDate ) return time;
      const dateLabel = this.formatDate(message.target_date);
      return dateLabel ? `${dateLabel} ${time}` : time;
    },
    formatDate( targetDate ) {
      return targetDate
        ? moment(targetDate, 'YYYY-MM-DD').format('DD/MM')
        : '';
    },
    formatTime( sendTime ) {
      return moment(sendTime, 'HH:mm').format('HH:mm');
    },
    truncateBody( body ) {
      return body && body.length > 40 ? `${body.slice(0, 40)}…` : body;
    }
  }
};
</script>

<style scoped>
/* px, never rem: this app sets html { font-size: 10px }. */
.organizer-message-outside-block {
  margin: 16px 0 24px;
  padding: 12px;
  border: 1px dashed #c9c2e4;
  border-radius: 8px;
}

.organizer-message-outside-title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #6c4fc7;
}

.organizer-message-day-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0 0 12px;
  padding: 0 15px;
}

.organizer-message-day-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid #6c4fc7;
  border-radius: 6px;
  background: rgba(108, 79, 199, 0.06);
  cursor: pointer;
  font-size: 14px;
}

.organizer-message-day-item:hover {
  background: rgba(108, 79, 199, 0.14);
}

.organizer-message-whatsapp-icon {
  width: 18px;
  height: 18px;
  margin-inline-end: 6px;
  vertical-align: middle;
}

.organizer-message-day-item-icon {
  margin-inline-end: 0;
  flex-shrink: 0;
}

.organizer-message-day-item-time {
  font-weight: 700;
  color: #6c4fc7;
  flex-shrink: 0;
}

/* px, never rem. The private one differs in colour and weight, not only in
   its words, so a note to yourself is never mistaken for a group message. */
.organizer-message-delivery {
  flex: 0 0 auto;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.6;
  white-space: nowrap;
}

/* Already delivered: quiet. Nothing is wrong and nothing is owed. */
.organizer-message-delivery.is-sent {
  background: #eef5f1;
  color: #40705a;
}

/* Never going out. Amber rather than red: it is not an error, and there is
   nothing to fix -- the moment simply passed. */
.organizer-message-delivery.is-missed {
  background: #fff6e5;
  color: #8a6100;
}

.organizer-message-recipient {
  font-size: 12px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #eef1f4;
  flex-shrink: 0;
}

.organizer-message-recipient.is-private {
  background: #fde9c8;
  font-weight: 600;
}

.organizer-message-day-item-body {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  color: #2E2E3A;
}

.organizer-message-day-item-delete {
  font-size: 16px;
  color: #dc3545;
  cursor: pointer;
  flex-shrink: 0;
}

/* The block has its own padding; the inner list's 15px would double it. */
.organizer-message-outside-block .organizer-message-day-list {
  padding: 0;
  margin-bottom: 0;
}
</style>
